import { useEffect, useRef, useCallback } from "react";

/**
 * KlydePlatformGraphic — Stripe-style animated flow diagram
 * 
 * Drop this component into your Lovable project.
 * 
 * Props:
 *   - logoSrc: path to the Klyde logo image (default: "/Logoblack.png")
 *   - outlookSrc, gmailSrc, wechatSrc, whatsappSrc, chatSrc: icon paths
 * 
 * SETUP:
 *   1. Copy this file to: src/components/KlydePlatformGraphic.tsx
 *   2. Place icon images in /public/ (outlook.png, gmail.png, etc.)
 *   3. Import and use: <KlydePlatformGraphic />
 */

interface KlydePlatformGraphicProps {
  logoSrc?: string;
  outlookSrc?: string;
  gmailSrc?: string;
  wechatSrc?: string;
  whatsappSrc?: string;
  chatSrc?: string;
}

interface AnimationPath {
  element: SVGCircleElement;
  pathElement: SVGPathElement;
  progress: number;
  speed: number;
  pathLength: number;
}

const PARTICLE_COUNT_PER_LINE = 3;
const PARTICLE_SPEED = 0.006;
const PARTICLE_RADIUS = 3;

const KlydePlatformGraphic = ({
  logoSrc = "/logo-black.png",
  outlookSrc = "/outlook.png",
  gmailSrc = "/gmail.png",
  wechatSrc = "/wechat.png",
  whatsappSrc = "/whatsapp.png",
  chatSrc = "/chat.png",
}: KlydePlatformGraphicProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const topSvgRef = useRef<SVGSVGElement>(null);
  const bottomSvgRef = useRef<SVGSVGElement>(null);
  const leftSvgRef = useRef<SVGSVGElement>(null);
  const hubRef = useRef<HTMLDivElement>(null);
  const sourceRowRef = useRef<HTMLDivElement>(null);
  const outputRowRef = useRef<HTMLDivElement>(null);
  const animationPathsRef = useRef<AnimationPath[]>([]);
  const animationIdRef = useRef<number | null>(null);

  const createSvgElement = (tag: string) =>
    document.createElementNS("http://www.w3.org/2000/svg", tag);

  const drawTopFlow = useCallback(() => {
    const svg = topSvgRef.current;
    const canvas = svg?.parentElement;
    const hub = hubRef.current;
    const sourceRow = sourceRowRef.current;
    if (!svg || !canvas || !hub || !sourceRow) return;

    svg.querySelectorAll("path, circle").forEach((el) => el.remove());

    const containerRect = canvas.getBoundingClientRect();
    const hubRect = hub.getBoundingClientRect();
    const hubCenterX = hubRect.left + hubRect.width / 2 - containerRect.left;
    const endY = hubRect.top - containerRect.top;

    const sources = sourceRow.querySelectorAll(".kpg-source-item");
    sources.forEach((src, i) => {
      const bubble = src.querySelector(".kpg-source-bubble");
      if (!bubble) return;
      const bubbleRect = bubble.getBoundingClientRect();
      const startX = bubbleRect.left + bubbleRect.width / 2 - containerRect.left;
      const midY = endY * 0.55;

      const d = `M ${startX} 0 C ${startX} ${midY}, ${hubCenterX} ${midY}, ${hubCenterX} ${endY}`;

      const path = createSvgElement("path") as unknown as SVGPathElement;
      path.setAttribute("d", d);
      path.setAttribute("class", "kpg-particle-path");
      path.setAttribute("stroke", "url(#kpg-lineGradTop)");
      svg.appendChild(path);

      for (let p = 0; p < PARTICLE_COUNT_PER_LINE; p++) {
        const circle = createSvgElement("circle") as unknown as SVGCircleElement;
        circle.setAttribute("r", String(PARTICLE_RADIUS));
        circle.setAttribute("class", i % 2 === 0 ? "kpg-particle" : "kpg-particle-secondary");
        svg.appendChild(circle);

        animationPathsRef.current.push({
          element: circle,
          pathElement: path,
          progress: p / PARTICLE_COUNT_PER_LINE + Math.random() * 0.1,
          speed: PARTICLE_SPEED + Math.random() * 0.002,
          pathLength: path.getTotalLength(),
        });
      }
    });
  }, []);

  const drawBottomFlow = useCallback(() => {
    const svg = bottomSvgRef.current;
    const canvas = svg?.parentElement;
    const hub = hubRef.current;
    const outputRow = outputRowRef.current;
    if (!svg || !canvas || !hub || !outputRow) return;

    svg.querySelectorAll("path, circle").forEach((el) => el.remove());

    const containerRect = canvas.getBoundingClientRect();
    const hubRect = hub.getBoundingClientRect();
    const hubCenterX = hubRect.left + hubRect.width / 2 - containerRect.left;
    const startY = hubRect.bottom - containerRect.top;

    const outputs = outputRow.querySelectorAll(".kpg-output-card");
    outputs.forEach((_out) => {
      const box = _out.querySelector(".kpg-output-box");
      if (!box) return;
      const boxRect = box.getBoundingClientRect();
      const endX = boxRect.left + boxRect.width / 2 - containerRect.left;
      const endY = containerRect.height;
      const midY = startY + (endY - startY) * 0.45;

      const d = `M ${hubCenterX} ${startY} C ${hubCenterX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;

      const path = createSvgElement("path") as unknown as SVGPathElement;
      path.setAttribute("d", d);
      path.setAttribute("class", "kpg-particle-path");
      path.setAttribute("stroke", "url(#kpg-lineGradBottom)");
      svg.appendChild(path);

      for (let p = 0; p < PARTICLE_COUNT_PER_LINE; p++) {
        const circle = createSvgElement("circle") as unknown as SVGCircleElement;
        circle.setAttribute("r", String(PARTICLE_RADIUS));
        circle.setAttribute("class", "kpg-particle");
        svg.appendChild(circle);

        animationPathsRef.current.push({
          element: circle,
          pathElement: path,
          progress: p / PARTICLE_COUNT_PER_LINE + Math.random() * 0.1,
          speed: PARTICLE_SPEED + Math.random() * 0.002,
          pathLength: path.getTotalLength(),
        });
      }
    });
  }, []);

  const drawSideConnectors = useCallback(() => {
    const leftSvg = leftSvgRef.current;
    if (!leftSvg) return;

    leftSvg.querySelectorAll("path, circle").forEach((el) => el.remove());

    const erpItems = document.querySelectorAll(".kpg-erp-item");
    erpItems.forEach((_item, i) => {
      const yPos = 30 + i * 58;
      const d = `M 0 ${yPos} C 30 ${yPos}, 30 130, 60 130`;

      const path = createSvgElement("path") as unknown as SVGPathElement;
      path.setAttribute("d", d);
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "url(#kpg-sideGrad)");
      path.setAttribute("stroke-width", "1.5");
      leftSvg.appendChild(path);

      const circle = createSvgElement("circle") as unknown as SVGCircleElement;
      circle.setAttribute("r", "2.5");
      circle.setAttribute("fill", "#4361ee");
      circle.setAttribute("opacity", "0.7");
      leftSvg.appendChild(circle);

      animationPathsRef.current.push({
        element: circle,
        pathElement: path,
        progress: Math.random(),
        speed: PARTICLE_SPEED + Math.random() * 0.002,
        pathLength: path.getTotalLength(),
      });
    });
  }, []);

  const animate = useCallback(() => {
    animationPathsRef.current.forEach((item) => {
      item.progress += item.speed;
      if (item.progress > 1) item.progress -= 1;

      const point = item.pathElement.getPointAtLength(item.progress * item.pathLength);
      item.element.setAttribute("cx", String(point.x));
      item.element.setAttribute("cy", String(point.y));

      const opacity = Math.sin(item.progress * Math.PI);
      item.element.setAttribute("opacity", (0.3 + opacity * 0.7).toFixed(2));
    });

    animationIdRef.current = requestAnimationFrame(animate);
  }, []);

  const init = useCallback(() => {
    animationPathsRef.current = [];
    if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);

    drawTopFlow();
    drawBottomFlow();
    drawSideConnectors();
    animate();
  }, [drawTopFlow, drawBottomFlow, drawSideConnectors, animate]);

  useEffect(() => {
    // Small delay to ensure DOM is painted
    const timer = setTimeout(init, 100);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(init, 200);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
    };
  }, [init]);

  return (
    <>
      <style>{`
        .kpg-container {
          position: relative;
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: hsl(var(--foreground));
          background: transparent;
          border-radius: 16px;
          padding: 32px 16px;
        }

        .kpg-source-row {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 0;
          position: relative;
          z-index: 10;
          flex-wrap: wrap;
        }

        .kpg-source-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          min-width: 72px;
        }

        .kpg-source-bubble {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .kpg-source-bubble::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(128,0,128,0.08) 0%, transparent 50%);
          pointer-events: none;
        }

        .kpg-source-bubble:hover {
          border-color: rgba(128,0,128,0.5);
          box-shadow: 0 0 20px rgba(128,0,128,0.15);
          transform: translateY(-3px);
        }

        .kpg-source-bubble img {
          width: 30px;
          height: 30px;
          object-fit: contain;
        }

        .kpg-source-bubble svg {
          width: 28px;
          height: 28px;
        }

        .kpg-source-label {
          font-size: 10px;
          font-weight: 600;
          color: hsl(var(--foreground));
          letter-spacing: 0.8px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .kpg-flow-canvas {
          width: 100%;
          position: relative;
          z-index: 5;
          overflow: visible;
        }

        .kpg-flow-canvas svg {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          overflow: visible;
        }

        .kpg-top-flow { height: 60px; }
        .kpg-bottom-flow { height: 60px; }

        .kpg-central-layout {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          position: relative;
          z-index: 10;
        }

        .kpg-side-panel {
          display: flex;
          flex-direction: column;
          gap: 14px;
          align-items: center;
          min-width: 130px;
          position: relative;
        }

        .kpg-side-panel-label {
          font-size: 10px;
          font-weight: 700;
          color: hsl(var(--foreground));
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .kpg-erp-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 14px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 10px;
          width: 140px;
          transition: all 0.3s ease;
        }

        .kpg-erp-item:hover {
          border-color: rgba(128,0,128,0.3);
          background: rgba(128,0,128,0.08);
        }

        .kpg-erp-icon {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 800;
          color: #fff;
          flex-shrink: 0;
        }

        .kpg-erp-icon-xero { background: #13B5EA; }
        .kpg-erp-icon-myob { background: #6200EA; }
        .kpg-erp-icon-odoo { background: #714B67; }
        .kpg-erp-icon-dynamics { background: #002050; }

        .kpg-erp-name {
          font-size: 12px;
          font-weight: 500;
          color: hsl(var(--foreground));
          white-space: nowrap;
        }

        .kpg-side-connectors {
          width: 60px;
          position: relative;
          flex-shrink: 0;
        }

        .kpg-side-connectors svg {
          width: 100%;
          height: 100%;
        }

        .kpg-center-hub {
          position: relative;
          z-index: 20;
        }

        .kpg-hub-card {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 20px;
          padding: 32px 48px;
          text-align: center;
          position: relative;
          overflow: hidden;
          box-shadow:
            0 0 60px rgba(128,0,128,0.08),
            0 4px 24px rgba(0,0,0,0.06);
          min-width: 280px;
        }

        .kpg-hub-card::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: conic-gradient(
            from 0deg,
            transparent,
            rgba(128,0,128,0.04),
            transparent,
            rgba(139,92,246,0.04),
            transparent
          );
          animation: kpg-hubRotate 20s linear infinite;
          pointer-events: none;
        }

        @keyframes kpg-hubRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .kpg-hub-logo {
          width: 180px;
          margin: 0 auto 16px;
          position: relative;
          z-index: 2;
        }

        .kpg-hub-logo img {
          width: 100%;
        }

        .dark .kpg-hub-logo img {
          filter: invert(1) brightness(2);
        }

        .kpg-hub-subtitle {
          font-size: 12px;
          font-weight: 500;
          color: hsl(var(--muted-foreground));
          letter-spacing: 2px;
          text-transform: uppercase;
          position: relative;
          z-index: 2;
        }

        .kpg-hub-status-bar {
          margin-top: 20px;
          display: flex;
          gap: 16px;
          justify-content: center;
          position: relative;
          z-index: 2;
        }

        .kpg-hub-status-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: hsl(var(--muted-foreground));
        }

        .kpg-hub-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          animation: kpg-statusPulse 2s ease-in-out infinite;
        }

        .kpg-hub-status-dot-green { background: #22c55e; box-shadow: 0 0 8px rgba(34,197,94,0.5); }
        .kpg-hub-status-dot-blue { background: #800080; box-shadow: 0 0 8px rgba(128,0,128,0.5); }
        .kpg-hub-status-dot-purple { background: #8b5cf6; box-shadow: 0 0 8px rgba(139,92,246,0.5); }

        @keyframes kpg-statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .kpg-output-row {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          gap: 48px;
          position: relative;
          z-index: 10;
          padding-left: 190px;
        }

        .kpg-output-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .kpg-output-box {
          width: 126px;
          height: 84px;
          border-radius: 14px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          transition: all 0.3s ease;
          position: relative;
        }

        .kpg-output-box::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(128,0,128,0.06) 0%, transparent 50%);
          pointer-events: none;
        }

        .kpg-output-box:hover {
          border-color: rgba(128,0,128,0.3);
          box-shadow: 0 0 24px rgba(128,0,128,0.1);
          transform: translateY(-2px);
        }

        .kpg-output-icon {
          width: 34px;
          height: 34px;
          stroke: hsl(var(--foreground));
          fill: none;
          stroke-width: 1.5;
        }

        .kpg-output-label {
          font-size: 12px;
          font-weight: 700;
          color: hsl(var(--foreground));
        }

        .kpg-output-sublabel {
          font-size: 9px;
          color: hsl(var(--muted-foreground));
          margin-top: -4px;
        }

        .kpg-particle-path {
          fill: none;
          stroke: rgba(128,0,128,0.15);
          stroke-width: 1.5;
        }

        .kpg-particle {
          fill: #800080;
          filter: url(#kpg-particleGlow);
        }

        .kpg-particle-secondary {
          fill: #8b5cf6;
          filter: url(#kpg-particleGlow);
        }

        @media (max-width: 768px) {
          .kpg-central-layout {
            flex-direction: column;
            gap: 24px;
          }
          .kpg-side-panel {
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
          }
          .kpg-side-connectors {
            display: none;
          }
          .kpg-source-row {
            gap: 12px;
          }
          .kpg-source-bubble {
            width: 48px;
            height: 48px;
          }
          .kpg-output-row {
            padding-left: 0;
          }
        }
      `}</style>

      <div className="kpg-container" ref={containerRef}>
        {/* TOP: Source Input Icons */}
        <div className="kpg-source-row" ref={sourceRowRef}>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <img src={outlookSrc} alt="Outlook" />
            </div>
            <span className="kpg-source-label">Outlook</span>
          </div>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <img src={gmailSrc} alt="Gmail" />
            </div>
            <span className="kpg-source-label">Gmail</span>
          </div>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <img src={wechatSrc} alt="WeChat" />
            </div>
            <span className="kpg-source-label">WeChat</span>
          </div>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <img src={whatsappSrc} alt="WhatsApp" />
            </div>
            <span className="kpg-source-label">WhatsApp</span>
          </div>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <svg viewBox="0 0 48 48" fill="none">
                <rect x="6" y="2" width="36" height="44" rx="4" fill="rgba(76,175,80,0.15)" stroke="#4caf50" strokeWidth="2" />
                <text x="24" y="30" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="10" fontWeight="700" fill="#4caf50">XLS</text>
              </svg>
            </div>
            <span className="kpg-source-label">Excel</span>
          </div>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <svg viewBox="0 0 48 48" fill="none">
                <rect x="6" y="2" width="36" height="44" rx="4" fill="rgba(229,57,53,0.15)" stroke="#e53935" strokeWidth="2" />
                <text x="24" y="30" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="10" fontWeight="700" fill="#e53935">PDF</text>
              </svg>
            </div>
            <span className="kpg-source-label">PDF</span>
          </div>
          <div className="kpg-source-item">
            <div className="kpg-source-bubble">
              <img src={chatSrc} alt="Klyde Chat" />
            </div>
            <span className="kpg-source-label">Klyde Chat</span>
          </div>
        </div>

        {/* TOP FLOW LINES */}
        <div className="kpg-flow-canvas kpg-top-flow">
          <svg ref={topSvgRef} width="100%" height="100%">
            <defs>
              <filter id="kpg-particleGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <linearGradient id="kpg-lineGradTop" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#800080" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#800080" stopOpacity="0.05" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* CENTRAL: LEFT ERPS + HUB */}
        <div className="kpg-central-layout">
          {/* LEFT: ERP Integrations */}
          <div className="kpg-side-panel">
            <div className="kpg-side-panel-label">Integrations</div>
            <div className="kpg-erp-item">
              <div className="kpg-erp-icon kpg-erp-icon-xero">X</div>
              <span className="kpg-erp-name">Xero</span>
            </div>
            <div className="kpg-erp-item">
              <div className="kpg-erp-icon kpg-erp-icon-myob">M</div>
              <span className="kpg-erp-name">MYOB</span>
            </div>
            <div className="kpg-erp-item">
              <div className="kpg-erp-icon kpg-erp-icon-odoo">O</div>
              <span className="kpg-erp-name">Odoo</span>
            </div>
            <div className="kpg-erp-item">
              <div className="kpg-erp-icon kpg-erp-icon-dynamics">D</div>
              <span className="kpg-erp-name">MS Dynamics</span>
            </div>
          </div>

          {/* LEFT SIDE CONNECTORS */}
          <div className="kpg-side-connectors">
            <svg ref={leftSvgRef} width="60" height="260" viewBox="0 0 60 260">
              <defs>
                <linearGradient id="kpg-sideGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#800080" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="#800080" stopOpacity="0.06" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* CENTER HUB */}
          <div className="kpg-center-hub" ref={hubRef}>
            <div className="kpg-hub-card">
              <div className="kpg-hub-logo">
                <img src={logoSrc} alt="Klyde" />
              </div>
              <div className="kpg-hub-subtitle">QUOTATION AGENT</div>
            </div>
          </div>
        </div>

        {/* BOTTOM FLOW LINES */}
        <div className="kpg-flow-canvas kpg-bottom-flow" style={{ paddingLeft: "190px" }}>
          <svg ref={bottomSvgRef} width="100%" height="100%">
            <defs>
              <linearGradient id="kpg-lineGradBottom" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#800080" stopOpacity="0.05" />
                <stop offset="100%" stopColor="#800080" stopOpacity="0.25" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* BOTTOM: Output Deliverables */}
        <div className="kpg-output-row" ref={outputRowRef}>
          <div className="kpg-output-card">
            <div className="kpg-output-box">
              <img src="/mcp.png" alt="Cost Plan" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
            </div>
            <div className="kpg-output-label">Cost Plan</div>
            <div className="kpg-output-sublabel">Bill of Quantities × Price</div>
          </div>
          <div className="kpg-output-card">
            <div className="kpg-output-box">
              <img src="/schedule.png" alt="Product Schedule" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
            </div>
            <div className="kpg-output-label">Product Schedule</div>
            <div className="kpg-output-sublabel">Images, Specs, Finishes</div>
          </div>
        </div>
      </div>
    </>
  );
};

export { KlydePlatformGraphic };
export default KlydePlatformGraphic;
